import React, { useState, useEffect } from 'react';
import { Bot, Loader2, AlertCircle, Code, Search, FileText, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { openAIService } from '../../services/openai';
import OpenAI from 'openai';

interface APIConfig {
  id: string;
  nombre: string;
  api_key: string;
  organization_id: string | null;
}

interface OpenAIModel {
  id: string;
  codigo: string;
  nombre: string;
}

interface Props {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  editingAssistant?: {
    id: string;
    nombre: string;
    descripcion: string;
    modelo: string;
    instructions: string;
    api_configuracion_id: string;
    tools: string[];
    assistant_id: string;
    openai_assistant_id?: string;
    avatar_url?: string;
    vector_store_id?: string;
    max_tokens?: number;
  } | null;
}

interface KnowledgeFile {
  file: File;
  fileId?: string;
}

export default function AssistantNew({ onSuccess, onError, editingAssistant }: Props) {
  const [apiConfigs, setApiConfigs] = useState<APIConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<OpenAIModel[]>([]);
  const [isCreatingVectorStore, setIsCreatingVectorStore] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const [newAssistant, setNewAssistant] = useState({
    name: '',
    instructions: '',
    model: '',
    description: '',
    tools: ['code_interpreter', 'file_search'],
    max_tokens: 4096
  });

  useEffect(() => {
    fetchAPIConfigs();
    fetchOpenAIModels();
  }, []);

  useEffect(() => {
    if (editingAssistant) {
      setNewAssistant({
        name: editingAssistant.nombre,
        instructions: editingAssistant.instructions || '',
        model: editingAssistant.modelo,
        description: editingAssistant.descripcion,
        tools: ['code_interpreter', 'file_search'],
        max_tokens: editingAssistant.max_tokens || 4096
      });
      setSelectedConfig(editingAssistant.api_configuracion_id);
      setAssistantId(editingAssistant.assistant_id);
      setAvatarUrl(editingAssistant.avatar_url || '');
    }
  }, [editingAssistant]);

  const fetchAPIConfigs = async () => {
    try {
      const { data: configs, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setApiConfigs(configs || []);

      if (configs && configs.length === 1 && !editingAssistant) {
        setSelectedConfig(configs[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching API configs:', error);
      setError('Error al cargar las configuraciones de API');
      onError?.('Error al cargar las configuraciones de API');
    }
  };

  const fetchOpenAIModels = async () => {
    try {
      setIsLoadingModels(true);
      const { data, error } = await supabase
        .from('openai_models')
        .select('*')
        .order('codigo');

      if (error) throw error;
      setAvailableModels(data || []);

      if (editingAssistant && editingAssistant.modelo) {
        setNewAssistant(prev => ({ ...prev, model: editingAssistant.modelo }));
      } else if (data && data.length > 0) {
        const defaultModel = data.find(m => m.codigo === 'gpt-4-1106-preview')?.codigo || 
                           data.find(m => m.codigo === 'gpt-4-turbo-preview')?.codigo || 
                           data.find(m => m.codigo === 'gpt-3.5-turbo')?.codigo || 
                           data[0].codigo;
        setNewAssistant(prev => ({ ...prev, model: defaultModel }));
      }
    } catch (error: any) {
      console.error('Error fetching models:', error);
      setError('Error al obtener los modelos disponibles');
      onError?.('Error al obtener los modelos disponibles');
    } finally {
      setIsLoadingModels(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    const invalidFiles = files.filter(file => !file.type.match(/(text\/|application\/pdf|application\/json)/));
    if (invalidFiles.length > 0) {
      setError('Solo se permiten archivos de texto, PDF y JSON');
      return;
    }

    const largeFiles = files.filter(file => file.size > 20 * 1024 * 1024);
    if (largeFiles.length > 0) {
      setError('Los archivos no deben superar los 20MB cada uno');
      return;
    }

    try {
      setIsUploadingFiles(true);
      setError('');

      const config = apiConfigs.find(c => c.id === selectedConfig);
      if (!config) throw new Error('Configuración no encontrada');

      const newFiles: KnowledgeFile[] = [];
      const fileIds: string[] = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('purpose', 'assistants');

        const response = await fetch('https://api.openai.com/v1/files', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.api_key}`,
            ...(config.organization_id && { 'OpenAI-Organization': config.organization_id })
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || `Error al subir el archivo ${file.name}`);
        }

        const data = await response.json();
        fileIds.push(data.id);
        newFiles.push({ file, fileId: data.id });
      }

      if (!editingAssistant) {
        setIsCreatingVectorStore(true);
        const vectorStoreId = await openAIService.createVectorStore(
          config.id,
          newAssistant.name,
          fileIds
        );
        setSuccess(`Vector store creada con ${files.length} archivo(s)`);

        for (const file of newFiles) {
          await supabase
            .from('vector_store_documents')
            .insert({
              file_id: file.fileId,
              nombre: file.file.name,
              tipo: 'general',
              estado: 'activo',
              vector_store_id: vectorStoreId
            });
        }
      }

      setKnowledgeFiles(prev => [...prev, ...newFiles]);
      setSuccess(`${files.length} archivo(s) subido(s) correctamente`);
    } catch (error: any) {
      console.error('Error uploading files:', error);
      setError(`Error al subir los archivos: ${error.message}`);
    } finally {
      setIsUploadingFiles(false);
      setIsCreatingVectorStore(false);
    }
  };

  const removeFile = async (index: number) => {
    const file = knowledgeFiles[index];
    
    try {
      if (file.fileId) {
        const config = apiConfigs.find(c => c.id === selectedConfig);
        if (!config) throw new Error('Configuración no encontrada');

        const response = await fetch(`https://api.openai.com/v1/files/${file.fileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${config.api_key}`,
            ...(config.organization_id && { 'OpenAI-Organization': config.organization_id })
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Error al eliminar el archivo');
        }
      }

      setKnowledgeFiles(prev => prev.filter((_, i) => i !== index));
      setSuccess('Archivo eliminado correctamente');
    } catch (error: any) {
      console.error('Error removing file:', error);
      setError(`Error al eliminar el archivo: ${error.message}`);
    }
  };

  const handleCreateOrUpdateAssistant = async () => {
    if (!selectedConfig) {
      const error = 'Por favor selecciona una configuración de API';
      setError(error);
      onError?.(error);
      return;
    }

    if (!newAssistant.name.trim() || !newAssistant.instructions.trim()) {
      const error = 'El nombre y las instrucciones son requeridos';
      setError(error);
      onError?.(error);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const config = apiConfigs.find(c => c.id === selectedConfig);
      if (!config) throw new Error('Configuración no encontrada');

      let openaiAssistantId = assistantId;
      let vectorStoreId = editingAssistant?.vector_store_id;

      if (editingAssistant && !vectorStoreId) {
        setIsCreatingVectorStore(true);
        vectorStoreId = await openAIService.createVectorStore(
          config.id,
          newAssistant.name
        );
        setSuccess('Vector store creada correctamente');
      } else if (!editingAssistant) {
        const fileIds = knowledgeFiles.map(f => f.fileId).filter(Boolean) as string[];
        if (fileIds.length > 0) {
          setIsCreatingVectorStore(true);
          vectorStoreId = await openAIService.createVectorStore(
            config.id,
            newAssistant.name,
            fileIds
          );
          setSuccess('Vector store creada con archivos');
        }
      }

      const assistantData = {
        name: newAssistant.name,
        instructions: newAssistant.instructions,
        model: newAssistant.model,
        description: newAssistant.description,
        tools: [
          { type: 'code_interpreter' },
          { type: 'file_search' }
        ],
        ...(vectorStoreId && {
          tool_resources: {
            file_search: {
              vector_store_ids: [vectorStoreId]
            }
          }
        })
      };

      if (editingAssistant && editingAssistant.openai_assistant_id) {
        const response = await fetch(`https://api.openai.com/v1/assistants/${editingAssistant.openai_assistant_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.api_key}`,
            'OpenAI-Beta': 'assistants=v2',
            ...(config.organization_id && { 'OpenAI-Organization': config.organization_id })
          },
          body: JSON.stringify(assistantData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Error al actualizar el asistente en OpenAI');
        }

        const data = await response.json();
        openaiAssistantId = data.id;

        const { error: dbError } = await supabase
          .from('asistentes')
          .update({
            nombre: newAssistant.name,
            descripcion: newAssistant.description,
            modelo: newAssistant.model,
            instructions: newAssistant.instructions,
            api_configuracion_id: config.id,
            tools: ['code_interpreter', 'file_search'],
            assistant_id: openaiAssistantId,
            openai_assistant_id: openaiAssistantId,
            avatar_url: avatarUrl,
            vector_store_id: vectorStoreId,
            max_tokens: newAssistant.max_tokens
          })
          .eq('id', editingAssistant.id);

        if (dbError) throw dbError;

        setSuccess('Asistente actualizado correctamente');
      } else {
        const response = await fetch('https://api.openai.com/v1/assistants', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.api_key}`,
            'OpenAI-Beta': 'assistants=v2',
            ...(config.organization_id && { 'OpenAI-Organization': config.organization_id })
          },
          body: JSON.stringify(assistantData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Error al crear el asistente en OpenAI');
        }

        const data = await response.json();
        openaiAssistantId = data.id;

        const { data: newAssistantData, error: dbError } = await supabase
          .from('asistentes')
          .insert([{
            nombre: newAssistant.name,
            descripcion: newAssistant.description,
            modelo: newAssistant.model,
            estado: 'activo',
            instructions: newAssistant.instructions,
            api_configuracion_id: config.id,
            tools: ['code_interpreter', 'file_search'],
            assistant_id: openaiAssistantId,
            openai_assistant_id: openaiAssistantId,
            avatar_url: avatarUrl || 'https://images.unsplash.com/photo-1464582883107-8adf2dca8a9f?w=400&auto=format&fit=crop&q=80',
            vector_store_id: vectorStoreId,
            max_tokens: newAssistant.max_tokens
          }])
          .select()
          .single();

        if (dbError) throw dbError;

        if (knowledgeFiles.length > 0) {
          const documentsToInsert = knowledgeFiles.map(file => ({
            asistente_id: newAssistantData.id,
            file_id: file.fileId!,
            nombre: file.file.name,
            tipo: 'general',
            estado: 'activo',
            vector_store_id: vectorStoreId
          }));

          const { error: docsError } = await supabase
            .from('vector_store_documents')
            .insert(documentsToInsert);

          if (docsError) throw docsError;
        }

        setSuccess('Asistente creado correctamente');
      }

      setAssistantId(openaiAssistantId);
      onSuccess?.();

    } catch (error: any) {
      console.error('Error creating/updating assistant:', error);
      const errorMessage = error.message || 'Error al procesar el asistente';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
      setIsCreatingVectorStore(false);
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('La imagen no debe superar los 2MB');
      return;
    }

    const uploadAvatar = async () => {
      try {
        setIsLoading(true);
        setError('');

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('assistant-avatars')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('assistant-avatars')
          .getPublicUrl(filePath);

        setAvatarUrl(publicUrl);
        setSuccess('Avatar actualizado correctamente');
      } catch (error: any) {
        console.error('Error uploading avatar:', error);
        setError('Error al subir el avatar');
      } finally {
        setIsLoading(false);
      }
    };

    uploadAvatar();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {editingAssistant ? 'Editar Asistente' : 'Crear Nuevo Asistente'}
      </h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {assistantId && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          ID del Asistente: {assistantId}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Avatar
          </label>
          <div className="flex items-center space-x-4">
            <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt="Avatar" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                PNG, JPG o GIF (máx. 2MB)
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Base de Conocimiento
          </label>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <input
                type="file"
                onChange={handleFileChange}
                accept=".txt,.md,.pdf,.json,.csv"
                multiple
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isUploadingFiles}
              />
              {isUploadingFiles && (
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              )}
            </div>
            
            {knowledgeFiles.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Archivos subidos:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {knowledgeFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{file.file.name}</span>
                        {file.fileId && (
                          <span className="text-xs text-gray-500">
                            (ID: {file.fileId})
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Configuración de API
          </label>
          <select
            value={selectedConfig}
            onChange={(e) => setSelectedConfig(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          >
            <option value="">Seleccionar configuración</option>
            {apiConfigs.map((config) => (
              <option key={config.id} value={config.id}>
                {config.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre del Asistente
          </label>
          <input
            type="text"
            value={newAssistant.name}
            onChange={(e) => setNewAssistant({...newAssistant, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ej: Asistente de Marketing"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <input
            type="text"
            value={newAssistant.description}
            onChange={(e) => setNewAssistant({...newAssistant, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Breve descripción del asistente"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instrucciones
          </label>
          <textarea
            value={newAssistant.instructions}
            onChange={(e) => setNewAssistant({...newAssistant, instructions: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={5}
            placeholder="Instrucciones detalladas para el asistente..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Modelo
          </label>
          <div className="relative">
            <select
              value={newAssistant.model}
              onChange={(e) => setNewAssistant({...newAssistant, model: e.target.value})}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                isLoadingModels ? 'bg-gray-100' : ''
              }`}
              disabled={isLoadingModels}
              required
            >
              <option value="">Seleccionar modelo</option>
              {availableModels.map(model => (
                <option key={model.id} value={model.codigo}>
                  {model.nombre} ({model.codigo})
                </option>
              ))}
            </select>
            {isLoadingModels && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Máximo de Tokens
          </label>
          <input
            type="number"
            value={newAssistant.max_tokens}
            onChange={(e) => setNewAssistant({...newAssistant, max_tokens: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="1"
            max="32768"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Límite máximo de tokens para las respuestas del asistente
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Herramientas Habilitadas
          </label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
              <Code className="w-5 h-5 text-blue-500" />
              <span className="text-gray-700">Code Interpreter</span>
              <div className="flex-1"></div>
              <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-500"></div>
            </div>
            <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
              <Search className="w-5 h-5 text-blue-500" />
              <span className="text-gray-700">File Search</span>
              <div className="flex-1"></div>
              <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-500"></div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={handleCreateOrUpdateAssistant}
            disabled={isLoading || isCreatingVectorStore || isUploadingFiles}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading || isCreatingVectorStore || isUploadingFiles ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{editingAssistant ? 'Actualizando...' : 'Creando...'}</span>
              </>
            ) : (
              <>
                <Bot className="w-5 h-5" />
                <span>{editingAssistant ? 'Actualizar Asistente' : 'Crear Asistente'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}